/**
 * Contexto da Proposta (B3): carrega, da API, a Proposta com as versões, a
 * Oportunidade, a Base de referências (catálogo, parceiros e entidades, Cliente)
 * e as listas. As telas portadas da DEMO-03 leem `d` com os mesmos nomes do
 * protótipo (produtos, grupos, entidades, clientes); as ações chamam a API.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { api } from '../../api.ts';
import { useSessao } from '../../sessao.tsx';
import { EstadoCarga, useCarga } from '../ui.tsx';
import type { Base, CondicaoPagamento, DadosEmpresa, ProdutoRef, Proposta, TermoComercial, TermosGerais, Tipo, Versao } from '../../../../backend/src/compartilhado/propostas.ts';
import type { Estagio } from '../../../../backend/src/compartilhado/oportunidades.ts';

export interface OportunidadeDaProposta {
  id: string;
  numero: string;
  titulo: string;
  tipo: Tipo;
  estagio: Estagio;
  clienteId: string;
  entidadeClienteId: string;
  responsavelId: string;
  previsaoFechamento: string;
}
export interface OpcoesProposta {
  hoje: string;
  condicoes_pagamento: CondicaoPagamento[];
  motivos_perda: string[];
  responsaveis: { id: string; nome: string; email: string }[];
  abatimento: { percentual: number; vigenteDesde: string } | null;
  papeis_fornecedor: string[];
  empresa: DadosEmpresa | null;
  termos_venda: TermoComercial[];
  /** Termos Gerais vigentes hoje (parâmetro comercial.termos_gerais); null quando não configurados. */
  termos_gerais: TermosGerais | null;
}
export interface DadosProposta {
  proposta: (Proposta & { atualizado_em: string | null }) | null;
  oportunidade: OportunidadeDaProposta;
  base: Base;
  opcoes: OpcoesProposta;
  /** B4: Pedido gerado da versão aceita. */
  pedido: { numero: string; versao: number } | null;
}

interface ValorContexto {
  dados: DadosProposta;
  d: Base;
  recarregar: () => Promise<void>;
  aplicar: (novo: DadosProposta) => void;
  adicionarProduto: (p: ProdutoRef) => void;
  /** Nome do responsável INTTRIX (a versão guarda o id do usuário). */
  nomeUsuario: (id: string) => string;
  eu: string;
}
const Contexto = createContext<ValorContexto | null>(null);

export function ProvedorProposta({ oportunidadeId, children }: { oportunidadeId: string; children: ReactNode }) {
  const { eu } = useSessao();
  const carga = useCarga(() => api.get<DadosProposta>(`/api/oportunidades/${oportunidadeId}/proposta`), [oportunidadeId]);
  const [extras, setExtras] = useState<ProdutoRef[]>([]);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="proposta" lixeira={carga.lixeira} />;
  const dados = carga.dados;
  const d: Base = { ...dados.base, produtos: [...dados.base.produtos, ...extras.filter((x) => !dados.base.produtos.some((p) => p.id === x.id))] };
  const valor: ValorContexto = {
    dados,
    d,
    recarregar: carga.recarregar,
    aplicar: (novo) => carga.setDados(novo),
    adicionarProduto: (p) => setExtras((xs) => [...xs, p]),
    nomeUsuario: (id) => dados.opcoes.responsaveis.find((r) => r.id === id)?.nome ?? '—',
    eu: eu?.nome ?? '',
  };
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useProposta() {
  const c = useContext(Contexto);
  if (!c) throw new Error('ProvedorProposta ausente');
  return c;
}

/** "Agora" no fuso da INTTRIX (AAAA-MM-DDTHH:MM), para as trilhas do documento (o servidor audita com o horário dele). */
export const agoraSaoPaulo = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 16).replace(' ', 'T');

export type { Versao };
